import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import {
  FaStore,
  FaUser,
  FaEnvelope,
  FaGlobe,
  FaLayerGroup,
  FaBarcode,
  FaChevronDown,
  FaTimes,
} from "react-icons/fa";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

import "../styles/UserManagement.css";
import { onboardOrganization, updateOrganization } from "../api/api";
import { useToast } from "../context/ToastContext";

const INITIAL_FORM = {
  orgName: "",
  username: "",
  contact: "92",
  email: "",
  website: "",
  feeType: "",
  feeValue: "",
};

const FEE_TYPE_OPTIONS = [
  { label: "Fixed Fee", value: "F" },
  { label: "Percentage Fee", value: "P" },
  { label: "Slab Based Fee", value: "S" },
];

export default function CreateOrganization({ mode: modeProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const mode = state.mode || modeProp || "create"; // create | edit | view
  const orgData = state.merchant || {}; // data passed from list

  const [loading, setLoading] = useState(false);
  const [feeTypeMenuOpen, setFeeTypeMenuOpen] = useState(false);
  const feeTypeMenuRef = useRef(null);
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    const handleDocMouseDown = (e) => {
      if (feeTypeMenuRef.current && !feeTypeMenuRef.current.contains(e.target)) {
        setFeeTypeMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocMouseDown);
    return () => document.removeEventListener("mousedown", handleDocMouseDown);
  }, []);

  const validationSchema = Yup.object().shape({
    orgName: Yup.string().required("Organization name is required"),
    username: Yup.string()
      .min(3, "Minimum 3 characters")
      .required("Username is required"),
    contact: Yup.string()
      .matches(/^[0-9]{10,15}$/, "Invalid contact number")
      .required("Contact is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    website: Yup.string()
      .url("Invalid website URL")
      .required("Website is required"),
    feeType: Yup.string()
      .oneOf(["F", "P", "S"], "Invalid fee type")
      .required("Fee type is required"),
    feeValue: Yup.string().required("Fee value is required"),
  });
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      let res;
      if (mode === "edit") {
        res = await updateOrganization(orgData.orgId, values);
      } else {
        res = await onboardOrganization(values);
      }

      if (res?.isSuccess) {
        showSuccess(
          mode === "edit" ? "Organization Updated" : "Organization Created",
          res?.message || `Organization ${mode === "edit" ? "updated" : "created"} successfully!`
        );
        navigate("/dashboard/organizationlist");
      } else {
        showError("Error", res?.message || "Operation failed", 3000);
      }
    } catch (err) {
      console.error("Organization Error:", err);
      showError("Error", "Something went wrong!", 3000);
    } finally {
      setLoading(false);
    }
  };

  const title = mode === "edit" ? "Edit Organization" : "Create Organization";
  const subtitle =
    mode === "edit"
      ? "Update organization details."
      : "Onboard a new institutional partner into the ecosystem.";
  const submitLabel =
    loading
      ? mode === "edit"
        ? "Updating..."
        : "Creating..."
      : mode === "edit"
        ? "Update Organization"
        : "Create Organization";

  const handleClose = () => navigate("/dashboard/organizationlist");

  return (
    <div className="um-modaloverlay" role="dialog" aria-modal="true" aria-label={title}>
      <button type="button" className="um-modaloverlay__scrim" aria-label={`Close ${title.toLowerCase()} dialog`} onClick={handleClose} />
      <Formik
        initialValues={{
          ...INITIAL_FORM,
          ...orgData,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue }) => (
          <Form className="um-adddialog-shell um-adddialog-shell--overlay">
            <div className="um-adddialog">
              <div className="um-adddialog__header">
                <div>
                  <h2 className="um-adddialog__title">{title}</h2>
                  <div className="um-adddialog__subtitle">{subtitle}</div>
                </div>
                <button type="button" className="um-adddialog__close" onClick={handleClose}>
                  <FaTimes />
                </button>
              </div>

              <div className="um-adddialog__body">
                <div className="um-adddialog__grid">
                  <div className="um-adddialog__field">
                    <label className="um-adddialog__label" htmlFor="org-name">Organization Name</label>
                    <div className="um-forminputwrap">
                      <FaStore className="um-forminputwrap__icon" />
                      <Field id="org-name" name="orgName" type="text" className="um-adddialog__input" placeholder="e.g. Apex Financial Group" disabled={mode === "view"} />
                    </div>
                    {touched.orgName && errors.orgName ? <div className="error-text">{errors.orgName}</div> : <div className="error-text" />}
                  </div>

                  <div className="um-adddialog__field">
                    <label className="um-adddialog__label" htmlFor="org-username">Username</label>
                    <div className="um-forminputwrap">
                      <FaUser className="um-forminputwrap__icon" />
                      <Field id="org-username" name="username" type="text" className="um-adddialog__input" placeholder="e.g. apex_fin" disabled={mode === "view"} />
                    </div>
                    {touched.username && errors.username ? <div className="error-text">{errors.username}</div> : <div className="error-text" />}
                  </div>

                  <div className="um-adddialog__field">
                    <label className="um-adddialog__label">Contact Number</label>
                    <PhoneInput
                      country="pk"
                      value={values.contact}
                      onChange={(value) => setFieldValue("contact", value.replace(/\D/g, ""))}
                      inputClass="um-adddialog__input"
                      countryCodeEditable={false}
                      disabled={mode === "view"}
                    />
                    {touched.contact && errors.contact ? <div className="error-text">{errors.contact}</div> : <div className="error-text" />}
                  </div>

                  <div className="um-adddialog__field">
                    <label className="um-adddialog__label" htmlFor="org-email">Email Address</label>
                    <div className="um-forminputwrap">
                      <FaEnvelope className="um-forminputwrap__icon" />
                      <Field id="org-email" name="email" type="email" className="um-adddialog__input" placeholder="ops@apexfin.com" disabled={mode === "view"} />
                    </div>
                    {touched.email && errors.email ? <div className="error-text">{errors.email}</div> : <div className="error-text" />}
                  </div>

                  <div className="um-adddialog__field um-adddialog__field--full">
                    <label className="um-adddialog__label" htmlFor="org-website">Website URL</label>
                    <div className="um-forminputwrap">
                      <FaGlobe className="um-forminputwrap__icon" />
                      <Field id="org-website" name="website" type="url" className="um-adddialog__input" placeholder="https://apexfin.com" disabled={mode === "view"} />
                    </div>
                    {touched.website && errors.website ? <div className="error-text">{errors.website}</div> : <div className="error-text" />}
                  </div>

                  <div className="um-adddialog__field">
                    <label className="um-adddialog__label">Fee Type</label>
                    <div className="um-formselectwrap" ref={feeTypeMenuRef}>
                      <button
                        type="button"
                        className={feeTypeMenuOpen ? "um-sortpill um-formselect open" : "um-sortpill um-formselect"}
                        onClick={() => {
                          if (mode === "view") return;
                          setFeeTypeMenuOpen((open) => !open);
                        }}
                        aria-haspopup="menu"
                        aria-expanded={feeTypeMenuOpen}
                        disabled={mode === "view"}
                      >
                        <span className="um-formselect__value">
                          {FEE_TYPE_OPTIONS.find((option) => option.value === values.feeType)?.label || "Select Fee Type"}
                        </span>
                        <FaChevronDown className={feeTypeMenuOpen ? "um-sortpill__icon open" : "um-sortpill__icon"} />
                      </button>

                      {feeTypeMenuOpen ? (
                        <div className="um-sortmenu um-formselectmenu" role="menu">
                          {FEE_TYPE_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              role="menuitem"
                              className={option.value === values.feeType ? "um-sortmenu__item active" : "um-sortmenu__item"}
                              onClick={() => {
                                setFieldValue("feeType", option.value);
                                setFieldValue("feeValue", "");
                                setFeeTypeMenuOpen(false);
                              }}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                    {touched.feeType && errors.feeType ? <div className="error-text">{errors.feeType}</div> : <div className="error-text" />}
                  </div>

                  <div className="um-adddialog__field">
                    <label className="um-adddialog__label" htmlFor="org-feevalue">Fee Value</label>
                    <div className="um-forminputwrap">
                      <FaBarcode className="um-forminputwrap__icon" />
                      <Field
                        id="org-feevalue"
                        name="feeValue"
                        type="text"
                        className="um-adddialog__input"
                        placeholder={
                          values.feeType === "F"
                            ? "Enter Fixed Amount"
                            : values.feeType === "P"
                              ? "Enter Percentage (%)"
                              : values.feeType === "S"
                                ? "Enter Fee Profile ID"
                                : "Enter Fee Value"
                        }
                        onInput={(e) => {
                          let value = e.target.value.replace(/[^0-9.]/g, "");
                          const parts = value.split(".");
                          if (parts.length > 2) value = parts[0] + "." + parts[1];
                          e.target.value = value;
                        }}
                        disabled={mode === "view"}
                      />
                    </div>
                    {touched.feeValue && errors.feeValue ? <div className="error-text">{errors.feeValue}</div> : <div className="error-text" />}
                  </div>
                </div>
              </div>

              {mode !== "view" && (
                <div className="um-adddialog__footer">
                  <button type="button" className="um-adddialog__cancel" onClick={handleClose}>Cancel</button>
                  <button type="submit" className="um-adddialog__submit" disabled={loading}>
                    {submitLabel}
                  </button>
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}
